import React, { useState, useEffect } from 'react';
import axiosClient, { UPLOADS_BASE_URL } from '../../axios_client';
import AdminMainLayout from './AdminMainLayout';
import AddAdminGallery from './AddAdminGallery';
import UpdateAdminGallery from './UpdateAdminGallery';

const AdminGallery = () => {
    const [galleryItems, setGalleryItems] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [selectedGalleryItem, setSelectedGalleryItem] = useState(null);
    const [error, setError] = useState('');
    const [expandedItem, setExpandedItem] = useState(null);

    // Define base URL for images (adjust for production)
    const BASE_URL = UPLOADS_BASE_URL; // Use https://sunflower-resort.com for production

    useEffect(() => {
        fetchGalleryItems();
    }, []);

    const fetchGalleryItems = async () => {
        try {
            const response = await axiosClient.get('/gallery/section/All', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            setGalleryItems(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch gallery items');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this gallery item?')) {
            try {
                await axiosClient.delete(`/gallery/${id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                fetchGalleryItems();
            } catch (err) {
                setError(err.response?.data?.error || 'Failed to delete gallery item');
            }
        }
    };

    const openUpdateModal = (item) => {
        setSelectedGalleryItem(item);
        setIsUpdateModalOpen(true);
    };

    const toggleItem = (id) => {
        setExpandedItem(expandedItem === id ? null : id);
    };

    // Normalize image URL to ensure it starts with a slash
    const getImageUrl = (img) => {
        if (!img) return '/fallback-image.jpg';
        const path = img.startsWith('/') ? img : `/${img}`;
        return `${BASE_URL}${path}`;
    };

    return (
        <AdminMainLayout>
            <div className="min-h-screen bg-[#F3ECDE] p-4 sm:p-6">
                <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-[#12392B]">
                    Manage Gallery
                </h1>
                {error && (
                    <p className="text-red-500 text-sm sm:text-base mb-3 sm:mb-4">
                        {error}
                    </p>
                )}
                <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="mb-4 bg-[#12392B] text-white p-2 sm:p-3 rounded hover:bg-[#E6A71A] hover:text-[#12392B] transition text-sm sm:text-base"
                >
                    Add New Gallery Item
                </button>

                {/* Small Screen: Collapsible Cards */}
                <div className="md:hidden">
                    {galleryItems.map((item) => (
                        <div
                            key={item.id}
                            className="bg-white p-4 rounded-lg shadow-md mb-4"
                        >
                            <div
                                className="flex justify-between items-center cursor-pointer"
                                onClick={() => toggleItem(item.id)}
                            >
                                <h2 className="text-lg sm:text-xl font-semibold text-[#12392B]">
                                    {item.section}
                                </h2>
                                <svg
                                    className="w-5 h-5 text-[#12392B]"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d={
                                            expandedItem === item.id
                                                ? 'M18 12H6'
                                                : 'M12 5v14m7-7H5'
                                        }
                                    />
                                </svg>
                            </div>
                            {expandedItem === item.id && (
                                <div className="mt-3">
                                    {item.image ? (
                                        <img
                                            src={getImageUrl(item.image)}
                                            alt={item.section}
                                            className="w-auto h-24 object-cover rounded mt-2"
                                            onError={(e) => {
                                                console.error(`Failed to load image: ${getImageUrl(item.image)}`);
                                                e.target.src = '/fallback-image.jpg';
                                            }}
                                        />
                                    ) : (
                                        <p className="text-gray-500 text-sm mt-2">No image available</p>
                                    )}
                                    <div className="mt-3 flex space-x-2">
                                        <button
                                            onClick={() => openUpdateModal(item)}
                                            className="bg-[#E6A71A] text-[#12392B] p-2 rounded hover:bg-[#12392B] hover:text-white transition text-sm"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(item.id)}
                                            className="bg-[#B91C1C] text-white p-2 rounded hover:bg-red-700 transition text-sm"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Medium and Larger Screens: Table */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg shadow-md">
                        <thead>
                            <tr className="bg-[#12392B] text-white">
                                <th className="p-3 text-left text-sm font-semibold">Section</th>
                                <th className="p-3 text-left text-sm font-semibold">Image</th>
                                <th className="p-3 text-left text-sm font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {galleryItems.map((item) => (
                                <tr key={item.id} className="border-b">
                                    <td className="p-3 text-[#12392B]">{item.section}</td>
                                    <td className="p-3">
                                        {item.image ? (
                                            <img
                                                src={getImageUrl(item.image)}
                                                alt={item.section}
                                                className="w-auto h-16 object-cover rounded"
                                                onError={(e) => {
                                                    console.error(`Failed to load image: ${getImageUrl(item.image)}`);
                                                    e.target.src = '/fallback-image.jpg';
                                                }}
                                            />
                                        ) : (
                                            <span className="text-gray-500">No image</span>
                                        )}
                                    </td>
                                    <td className="p-3 flex space-x-2">
                                        <button
                                            onClick={() => openUpdateModal(item)}
                                            className="bg-[#E6A71A] text-[#12392B] p-2 rounded hover:bg-[#12392B] hover:text-white transition text-sm"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(item.id)}
                                            className="bg-[#B91C1C] text-white p-2 rounded hover:bg-red-700 transition text-sm"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <AddAdminGallery
                    isOpen={isAddModalOpen}
                    onClose={() => setIsAddModalOpen(false)}
                    onGalleryItemAdded={fetchGalleryItems}
                />
                <UpdateAdminGallery
                    isOpen={isUpdateModalOpen}
                    onClose={() => setIsUpdateModalOpen(false)}
                    galleryItemData={selectedGalleryItem}
                    onGalleryItemUpdated={fetchGalleryItems}
                />
            </div>
        </AdminMainLayout>
    );
};

export default AdminGallery;